export type MachineState = 'available' | 'in_use' | 'maintenance' | 'unavailable';

export type MachineType = 
	| 'lawn_mower' 
	| 'tiller' 
	| 'trimmer' 
	| 'hedge_cutter' 
	| 'chainsaw'
	| 'excavator'
	| 'loader'
	| 'dump_truck'
	| 'compactor'
	| 'trencher';

export type Machine = {
	id: string;
	name: string;
	type: MachineType;
	state: MachineState;
	organizationId: string; // which company owns this machine
	notes?: string;
	lastMaintenanceDate?: string; // ISO date
	nextMaintenanceDate?: string; // ISO date
};

// Synthetic machines for demo
export const machines: Machine[] = [
	{
		id: 'mach1',
		name: 'Excavator #1',
		type: 'excavator',
		state: 'available',
		organizationId: 'org1',
		notes: 'Heavy-duty excavator for large projects',
		lastMaintenanceDate: new Date(Date.now() - 86400000 * 30).toISOString(),
		nextMaintenanceDate: new Date(Date.now() + 86400000 * 60).toISOString()
	},
	{
		id: 'mach2',
		name: 'Lawn Mower #1',
		type: 'lawn_mower',
		state: 'available',
		organizationId: 'org1',
		notes: 'Standard lawn mower',
		lastMaintenanceDate: new Date(Date.now() - 86400000 * 7).toISOString()
	},
	{
		id: 'mach3',
		name: 'Tiller #1',
		type: 'tiller',
		state: 'available',
		organizationId: 'org1',
		notes: 'Garden tiller for soil preparation',
		lastMaintenanceDate: new Date(Date.now() - 86400000 * 14).toISOString()
	},
	{
		id: 'mach4',
		name: 'Dump Truck #1',
		type: 'dump_truck',
		state: 'in_use',
		organizationId: 'org1',
		notes: 'For material transport',
		lastMaintenanceDate: new Date(Date.now() - 86400000 * 45).toISOString()
	},
	{
		id: 'mach5',
		name: 'Hedge Cutter #1',
		type: 'hedge_cutter',
		state: 'available',
		organizationId: 'org1',
		notes: 'Electric hedge trimmer',
		lastMaintenanceDate: new Date(Date.now() - 86400000 * 10).toISOString()
	},
	{
		id: 'mach6',
		name: 'Excavator #2',
		type: 'excavator',
		state: 'maintenance',
		organizationId: 'org2',
		notes: 'Scheduled maintenance',
		lastMaintenanceDate: new Date(Date.now() - 86400000 * 90).toISOString(),
		nextMaintenanceDate: new Date(Date.now() + 86400000 * 5).toISOString()
	},
	{
		id: 'mach7',
		name: 'Loader #1',
		type: 'loader',
		state: 'available',
		organizationId: 'org2',
		notes: 'Front-end loader',
		lastMaintenanceDate: new Date(Date.now() - 86400000 * 20).toISOString()
	},
	{
		id: 'mach8',
		name: 'Trimmer #1',
		type: 'trimmer',
		state: 'available',
		organizationId: 'org2',
		notes: 'String trimmer for edges',
		lastMaintenanceDate: new Date(Date.now() - 86400000 * 5).toISOString()
	},
	{
		id: 'mach9',
		name: 'Chainsaw #1',
		type: 'chainsaw',
		state: 'available',
		organizationId: 'org1',
		notes: 'Professional chainsaw',
		lastMaintenanceDate: new Date(Date.now() - 86400000 * 3).toISOString()
	},
	{
		id: 'mach10',
		name: 'Compactor #1',
		type: 'compactor',
		state: 'available',
		organizationId: 'org1',
		notes: 'Soil compactor for hardscaping',
		lastMaintenanceDate: new Date(Date.now() - 86400000 * 15).toISOString()
	}
];

// Load machines from localStorage
function loadMachines(): Machine[] {
	const stored = localStorage.getItem('machines');
	if (stored) {
		try {
			const parsed = JSON.parse(stored) as Machine[];
			return parsed;
		} catch {
			// Ignore and use defaults
		}
	}
	return machines;
}

export let machineData: Machine[] = loadMachines();

// Save machines to localStorage
function saveMachines() {
	try {
		localStorage.setItem('machines', JSON.stringify(machineData));
		window.dispatchEvent(new CustomEvent('machinesUpdated'));
	} catch (err) {
		console.error('Failed to save machines to localStorage', err);
	}
}

// Get all machines
export function getMachines(): Machine[] {
	machineData = loadMachines();
	return machineData;
}

// Get machines by organization
export function getMachinesByOrganization(organizationId: string): Machine[] {
	return machineData.filter(m => m.organizationId === organizationId);
}

// Get machine by ID
export function getMachineById(id: string): Machine | undefined {
	return machineData.find(m => m.id === id);
}

// Update machine
export function updateMachine(machineId: string, updates: Partial<Machine>): void {
	const index = machineData.findIndex(m => m.id === machineId);
	if (index === -1) return;
	
	machineData[index] = {
		...machineData[index],
		...updates
	};
	saveMachines();
}

// Create machine
export function createMachine(machine: Omit<Machine, 'id'>): Machine {
	const newMachine: Machine = {
		...machine,
		id: `mach-${Date.now()}`
	};
	machineData.push(newMachine);
	saveMachines();
	return newMachine;
}

// Delete machine
export function deleteMachine(machineId: string): void {
	machineData = machineData.filter(m => m.id !== machineId);
	saveMachines();
}

// Get machine type display name
export function getMachineTypeDisplayName(type: MachineType): string {
	const names: Record<MachineType, string> = {
		lawn_mower: 'Lawn Mower',
		tiller: 'Tiller',
		trimmer: 'Trimmer',
		hedge_cutter: 'Hedge Cutter',
		chainsaw: 'Chainsaw',
		excavator: 'Excavator',
		loader: 'Loader',
		dump_truck: 'Dump Truck',
		compactor: 'Compactor',
		trencher: 'Trencher'
	};
	return names[type] || type;
}

// Get machine state display name
export function getMachineStateDisplayName(state: MachineState): string {
	const names: Record<MachineState, string> = {
		available: 'Available',
		in_use: 'In Use',
		maintenance: 'Maintenance',
		unavailable: 'Unavailable'
	};
	return names[state] || state;
}

